import React, {useState, useContext, useEffect, useCallback} from 'react';
import Panel from '../../../components/Panel';
import FormInput from '../../../components/FormInput';
import RoundedButton from '../../../components/RoundedButton';
import AuthContext from '../../../contexts/AuthContext';
import useAPI from '../../../hooks/useAPI';
import Select from "../../../components/Select";
import FileUpload from "../../../components/FileUpload";
import DateInput from "../../../components/DateInput";
import Table from "../../../components/Table";
import Page from "../../../components/Page";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faDownload} from '@fortawesome/free-solid-svg-icons';

const MeterReadPage = () => {
    const { user, system } = useContext(AuthContext);
    const api = useAPI();

    const [loading, setLoading] = useState(true);
    const [systemOwnerName, setSystemOwnerName] = useState(`${user.first_name} ${user.last_name}` || '');
    const [selfGenerationIdCode, setSelfGenerationIdCode] = useState(system.resource.tracking_system_ref_id || '');
    const [meterReadDate, setMeterReadDate] = useState('');
    const [meterReadValue, setMeterReadValue] = useState('');
    const [meterReadUnit, setMeterReadUnit] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [meterReadData, setMeterReadData] = useState([]);

    const toCamelCase = (str) => {
        return str.replace(/([-_][a-z])/g, (group) =>
            group.toUpperCase()
                .replace('-', '')
                .replace('_', '')
        );
    };

    const handleSubmit = () => {
        let _errors = {};

        if (!systemOwnerName) {
            _errors.refName = 'System Owner Name required.';
        }

        if (!meterReadDate) {
            _errors.meterReadDate = 'Meter/Inverter Read Date required.';
        }

        if (!meterReadValue) {
            _errors.meterReadValue = 'Meter/Inverter Reading required.';
        }

        if (!meterReadUnit) {
            _errors.meterReadUnit = 'Meter/Inverter Metric required.';
        }

        if (attachments.length === 0) {
            _errors.attachments = 'At least one attachment is required.';
        }

        setErrors(_errors);
        if (Object.keys(_errors).length) {
            return;
        }

        setSaving(true);

        api.json('POST', `/systems/${system.id}/meter-reads`, {
            ref_name: systemOwnerName,
            tracking_system_ref_id: selfGenerationIdCode,
            meter_read_date: meterReadDate,
            meter_read_value: meterReadValue,
            meter_read_unit: meterReadUnit,
            attachments: attachments,
        }, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then((r) => r.json())
            .then((r) => {
                if (r.errors) {
                    Object.keys(r.errors).forEach((field) => {
                        const camelCaseField = toCamelCase(field);
                        if (camelCaseField.startsWith('attachments')) {
                            const errorMessage = r.errors[field][0];
                            _errors.attachments = errorMessage.replace(/attachments\.\d+/, 'attachments');
                        } else {
                            _errors[camelCaseField] = r.errors[field][0];
                        }
                    });
                    setErrors(_errors);
                    return;
                }

                if (r.data?.success) {
                    window.location.reload(true);
                } else {
                    alert('Unable to submit form. Try again later.');
                }
            }).catch((e) => alert(e))
            .finally(() => setSaving(false));
    };

    const download = useCallback((e) => {
        e.preventDefault();
        const url = e.currentTarget.href.replace(process.env.REACT_APP_API_URL, '');
        const fileName = e.currentTarget.text.trim();

        api.download(url, {}, fileName).catch((e) => alert(e));
    }, [api]);

    const load = ()=>{
        setLoading(true);

        api.json('GET', `/systems/${system.id}/meter-reads`)
            .then(r=>r.json())
            .then((r)=>{
                setMeterReadData(r.data || []);
            })
            .catch(e=>alert(e))
            .then(()=>{
                setLoading(false);
            })
    }

    useEffect(()=>{
        load();
        // eslint-disable-next-line
    }, []);

    const columns = React.useMemo(
        () => [
            {
                Header: 'Self-Generation ID Code',
                accessor: 'tracking_system_ref_id',
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {selfGenerationIdCode}
                    </div>
                ),
            },
            {
                Header: 'Meter/Inverter Read Date',
                accessor: 'meter_read_date',
            },
            {
                Header: 'Meter/Inverter Reading',
                accessor: 'meter_read_value',
            },
            {
                Header: 'Meter/Inverter Metric',
                accessor: 'meter_read_unit',
            },
            {
                Header: 'Photo of Meter Reading',
                accessor: 'attachments',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {row.original.attachments.map((attachment, index) => (
                            <a
                                key={index}
                                href={attachment.original_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                onClick={e => download(e)}
                                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                                <FontAwesomeIcon icon={faDownload} /> {attachment.original_name}
                            </a>
                        ))}
                    </div>
                ),
            },
        ],
        [selfGenerationIdCode, download]
    );

    return (
        <Page
            title="Meter Read Reporting"
        >
            <div className='meter-read-form'>
                <Panel
                    title="Meter Read Form"
                    headerStyle={{
                        borderBottomWidth: 1,
                        borderStyle: 'solid',
                        borderColor: '#E2E3E8',
                        paddingBottom: 20
                    }}
                    contentStyle={{
                        paddingTop: 20
                    }}
                >
                    <div className='account-settings--content'>
                        <div className='row'>
                            <div className='col-12 col-lg-12'>
                                <FormInput
                                    label={'System Owner Name'}
                                    placeholder="System Owner Name"
                                    required
                                    onChange={(e) => {
                                        setSystemOwnerName(e.target.value);
                                        setErrors({
                                            ...errors,
                                            refName: ''
                                        });
                                    }}
                                    disabled={saving}
                                    value={systemOwnerName}
                                    error={errors.refName}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 col-lg-12'>
                                <FormInput
                                    label={'Self-Generation ID Code'}
                                    placeholder="Provided by CSG (typically NON ID)"
                                    onChange={(e) => {
                                        setSelfGenerationIdCode(e.target.value);
                                        setErrors({
                                            ...errors,
                                            selfGenerationIdCode: ''
                                        });
                                    }}
                                    disabled={true}
                                    value={selfGenerationIdCode}
                                    error={errors.selfGenerationIdCode}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 col-lg-12'>
                                <DateInput
                                    label={'Meter/Inverter Read Date'}
                                    description="Date of the reading"
                                    required
                                    onChange={(e) => {
                                        setMeterReadDate(e.target.value);
                                        setErrors({
                                            ...errors,
                                            meterReadDate: ''
                                        });
                                    }}
                                    disabled={saving}
                                    value={meterReadDate}
                                    error={errors.meterReadDate}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 col-lg-12'>
                                <FormInput
                                    label={'Meter/Inverter Reading'}
                                    placeholder="The total energy produced by your system as of the date selected above"
                                    required
                                    onChange={(e) => {
                                        setMeterReadValue(e.target.value);
                                        setErrors({
                                            ...errors,
                                            meterReadValue: ''
                                        });
                                    }}
                                    disabled={saving}
                                    value={meterReadValue}
                                    error={errors.meterReadValue}
                                    maxLength="40"
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 col-lg-12'>
                                <Select
                                    label="Meter/Inverter Metric"
                                    value={meterReadUnit}
                                    required
                                    options={[
                                        {value: '', label: '-'},
                                        {value: 'kWh', label: 'kWh'},
                                        {value: 'MWh', label: 'MWh'},
                                        {value: 'Wh', label: 'Wh'},
                                    ]}
                                    onChange={(e) => setMeterReadUnit(e.target.value)}
                                    error={errors.meterReadUnit}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 col-lg-12'>
                                <FileUpload
                                    label="Photo of Meter Reading"
                                    placeholder="Add files or drop files here"
                                    required
                                    error={errors.attachments}
                                    onFileChange={(attachments) => {
                                        setAttachments(attachments);
                                        setErrors({
                                            ...errors,
                                            attachments: ''
                                        });
                                    }}
                                    multiple={true}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12'>
                                <RoundedButton
                                    solid
                                    onClick={handleSubmit}
                                    busy={saving}
                                >
                                    Submit
                                </RoundedButton>
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>
            {!loading && (
                <>
                    <p style={{fontSize: '20px', textAlign: 'center'}}>Previous Reading History</p>
                    <br/>
                    <Table columns={columns} data={meterReadData} rowsPerPage={0}/>
                </>
            )}
            <br/>
        </Page>
    );
};

export default MeterReadPage;
